"use client";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";
import { Toaster } from "@/shared/ui/sonner";
import {
  CommandPaletteProvider,
  GlobalCommandPalette
} from "@/shared/components/command-palette";
import { AuthProvider } from "@/features/auth/components/AuthProvider";
import { ThemeProvider } from "@/shared/lib/theme";
import { IconContext } from "@phosphor-icons/react";

export function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 1000 * 60, // 1 minute
            refetchOnWindowFocus: false,
          },
        },
      })
  );

  return (
    <ThemeProvider>
      {/* Phosphor icon defaults, set once. `size="1em"` makes icons inherit
          font-size so the shadcn sizing selectors (e.g. button.tsx's
          `[&_svg:not([class*='size-'])]:size-4`) and explicit `h-4 w-4`
          classNames keep driving geometry from CSS, exactly as they did
          before. Weight is the visual register: `regular` everywhere,
          `bold`/`fill` opted into per-site for active or filled states. */}
      <IconContext.Provider value={{ size: "1em", weight: "regular" }}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <CommandPaletteProvider>
              {children}
              <GlobalCommandPalette />
            </CommandPaletteProvider>
            <Toaster
              position="bottom-right"
              toastOptions={{
                style: {
                  background: "var(--card)",
                  border: "1px solid var(--border-strong)",
                  color: "var(--foreground)",
                  boxShadow: "var(--shadow-3)",
                },
              }}
            />
          </AuthProvider>
        </QueryClientProvider>
      </IconContext.Provider>
    </ThemeProvider>
  );
}
